import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BedDouble, Bath, MapPin, KeyRound, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* Keystone Realty — the listings website rendered on in-game property monitors
   (/cef/realty/:serverId). Public to browse (monitors have no session); buying
   needs a player token in the URL. Sold stock keeps showing with the ribbon. */

/* CSS-art hero — a house silhouette over a hue-tinted sky. No image assets, CEF-safe. */
function HeroArt({ hue, kind, sold }) {
  return (
    <div className="relative h-40 overflow-hidden" style={{ background: `linear-gradient(180deg, hsl(${hue},45%,72%) 0%, hsl(${hue},50%,58%) 62%, hsl(${hue},38%,34%) 62.5%, hsl(${hue},35%,26%) 100%)` }}>
      {/* sun */}
      <div className="absolute rounded-full" style={{ width: 44, height: 44, right: 26, top: 18, background: `hsl(${hue + 30},80%,88%)`, opacity: 0.9 }} />
      {/* skyline block for apartments/penthouse */}
      {["Apartment", "Studio", "Penthouse"].includes(kind) && (
        <>
          <div className="absolute bottom-0 left-6 w-10 bg-black/25" style={{ height: 92 }} />
          <div className="absolute bottom-0 left-[70px] w-8 bg-black/20" style={{ height: 66 }} />
        </>
      )}
      {/* house */}
      <svg viewBox="0 0 120 80" className="absolute bottom-0 left-1/2 -translate-x-1/2 w-40">
        <rect x="30" y="38" width="60" height="42" fill="hsl(30,20%,18%)" />
        <polygon points="24,40 60,12 96,40" fill="hsl(20,25%,14%)" />
        <rect x="52" y="56" width="16" height="24" fill="hsl(35,30%,30%)" />
        <rect x="38" y="46" width="10" height="10" fill={`hsl(${hue},60%,70%)`} opacity="0.9" />
        <rect x="72" y="46" width="10" height="10" fill={`hsl(${hue},60%,70%)`} opacity="0.9" />
      </svg>
      {sold && (
        <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
          <span data-testid="realty-sold" className="font-sans font-black text-2xl tracking-[0.25em] text-white border-4 border-white px-5 py-1.5 -rotate-6">SOLD</span>
        </div>
      )}
    </div>
  );
}

export default function RealtyCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const [data, setData] = useState(null);
  const [buying, setBuying] = useState("");

  const load = useCallback(() => {
    api.get(`/nc/servers/${serverId}/realty/listings`)
      .then((r) => setData(r.data))
      .catch(() => setData({ agency: "Keystone Realty", listings: [] }));
  }, [serverId]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // monitor display: the wall screen is opaque (it's a website, not a world overlay)
  useEffect(() => {
    document.documentElement.style.background = "#f5f1e8";
    document.body.style.background = "#f5f1e8";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  const buy = async (p) => {
    if (buying) return;
    if (demo) { toast.info("Preview monitor — buy from your phone or a live session"); return; }
    setBuying(p.id);
    try {
      const r = await api.post(`/nc/servers/${serverId}/realty/buy/${p.id}`, {}, { params: { token } });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBuying(""); }
  };

  return (
    <div data-testid="realty-page" className="min-h-screen" style={{ background: "#f5f1e8", color: "#1c1917" }}>
      {demo && <PreviewRibbon what="realty monitor" />}
      {/* masthead */}
      <header className="border-b-4 double" style={{ borderColor: "#1c1917" }}>
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-end justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-stone-500">est. 1962 · bornwood</p>
            <h1 className="font-serif text-5xl font-black tracking-tight mt-1" style={{ fontFamily: "Georgia, serif" }}>Keystone Realty</h1>
          </div>
          <div className="text-right">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">listings desk</p>
            <p className="font-serif text-2xl font-bold" style={{ fontFamily: "Georgia, serif" }}>{data?.listings?.filter((l) => l.status === "listed").length ?? "—"} on the books</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {!data && <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {(data?.listings || []).map((p) => (
            <article key={p.id} data-testid={`realty-card-${p.key}`} className="bg-white rounded-xl overflow-hidden shadow-[0_2px_12px_rgba(28,25,23,0.12)] border border-stone-200 flex flex-col">
              <HeroArt hue={p.hue} kind={p.kind} sold={p.status === "sold"} />
              <div className="p-5 flex flex-col gap-2 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-stone-400">{p.kind} · {p.area}</p>
                    <h2 className="font-serif text-xl font-bold mt-0.5" style={{ fontFamily: "Georgia, serif" }}>{p.title}</h2>
                  </div>
                  <p className="font-serif text-lg font-black whitespace-nowrap" style={{ fontFamily: "Georgia, serif", color: "#166534" }}>
                    ${p.price.toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-4 font-mono text-[10px] text-stone-500">
                  {p.beds > 0 && <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{p.beds} bd</span>}
                  <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{p.baths} ba</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{p.area}</span>
                </div>
                <p className="font-serif italic text-sm text-stone-600 leading-relaxed" style={{ fontFamily: "Georgia, serif" }}>{p.blurb}</p>
                <div className="mt-auto pt-3">
                  {p.status === "sold" ? (
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-400">settled{p.owner_name ? ` · ${p.owner_name}` : ""}</p>
                  ) : (
                    <button data-testid={`realty-buy-${p.key}`} onClick={() => buy(p)} disabled={!!buying}
                      className="w-full rounded-lg py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-white transition-transform active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                      style={{ background: "#1c1917" }}>
                      {buying === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                      {demo ? "Enquire" : "Buy — settle from bank"}
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
        <p className="text-center font-mono text-[9px] uppercase tracking-[0.3em] text-stone-400 mt-10">
          keystone realty · 14 ledger lane · licensed under the bornwood lands act
        </p>
      </main>
    </div>
  );
}
